import { Link } from 'react-router-dom';
import TopNav from '../components/TopNav';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Shield, Check, X, Mail } from 'lucide-react';

const LAST_UPDATED = 'April 24, 2026';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Privacy Policy - LinkDiry"
        description="What data the LinkDiry website and Chrome extension collect, how it's used, and how to contact us."
        canonical="/privacy"
      />
      <TopNav />

      <div className="max-w-3xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand-50 border border-brand-100 rounded-full text-xs font-semibold text-brand-700 uppercase tracking-wider mb-4">
            <Shield className="w-3.5 h-3.5" /> Privacy Policy
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-3">Privacy policy</h1>
          <p className="text-slate-500">Last updated {LAST_UPDATED}</p>
        </div>

        {/* TL;DR card */}
        <div className="mb-10 p-6 rounded-2xl bg-gradient-to-br from-brand-50 to-teal-50 border border-brand-200">
          <h2 className="text-lg font-bold text-slate-900 mb-3">The short version</h2>
          <ul className="space-y-2 text-sm text-slate-700">
            <li className="flex gap-2"><Check className="w-4 h-4 text-brand-600 flex-shrink-0 mt-0.5" /> We look up domain names to show guest post prices. That is the whole job.</li>
            <li className="flex gap-2"><Check className="w-4 h-4 text-brand-600 flex-shrink-0 mt-0.5" /> No ads. No data brokers. We do not sell anything.</li>
            <li className="flex gap-2"><Check className="w-4 h-4 text-brand-600 flex-shrink-0 mt-0.5" /> The extension reads domain names from pages. Not page content, form inputs, or full URLs.</li>
            <li className="flex gap-2"><X className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" /> We do not know your name or email unless you sign up.</li>
          </ul>
        </div>

        {/* Sections */}
        <div className="prose-slate space-y-10">

          <Section title="1. Who we are">
            <p>LinkDiry compares guest post and backlink prices across 50+ marketplaces, free. This policy covers both the website at <a href="https://linkdiry.com" className="text-brand-600 hover:underline">linkdiry.com</a> and the Chrome extension.</p>
          </Section>

          <Section title="2. Website data">
            <p className="mb-3">When you use the site, here is what we log:</p>
            <ul className="list-disc pl-6 space-y-1.5 text-slate-700">
              <li>The domain names you search. We need them to return a result. We also use aggregate totals to tune rankings.</li>
              <li>Your IP address and user agent, stored with each request. That is standard for any web host. We use it for rate limiting.</li>
              <li>Click-throughs to marketplaces. We record which seller and which domain you clicked. No personal identifier goes with it.</li>
              <li>If you sign up, we store your email and a hashed password. That is used for login and saved lists.</li>
            </ul>
          </Section>

          <Section title="3. Extension data">
            <p className="mb-3">When the extension is running:</p>
            <ul className="list-disc pl-6 space-y-1.5 text-slate-700">
              <li>The content script looks at links and visible text for anything that looks like a domain (strings like <code className="px-1.5 py-0.5 bg-slate-100 rounded">example.com</code>) and sends that list to our API for prices. It does not send full URLs, page titles, form data, or page content.</li>
              <li>When you open the popup, it reads the hostname of your current tab so the "Check current page" button works. That stays on your machine unless you click.</li>
              <li>Click events work the same way as the website: seller ID plus domain, no personal identifier.</li>
            </ul>
            <p className="mt-4 mb-2">Permissions we request:</p>
            <ul className="list-disc pl-6 space-y-1.5 text-slate-700">
              <li><code className="px-1.5 py-0.5 bg-slate-100 rounded">activeTab</code>. Reads the hostname of the tab you are on when the popup opens.</li>
              <li><code className="px-1.5 py-0.5 bg-slate-100 rounded">host_permissions</code> for <code className="px-1.5 py-0.5 bg-slate-100 rounded">https://api.linkdiry.com/*</code>. Lets the extension call our API to look up prices and record click-throughs.</li>
              <li><code className="px-1.5 py-0.5 bg-slate-100 rounded">content script matches</code>. The content script runs on a specific list of sites only: the SEO research tools (Ahrefs, SEMrush, Moz, Majestic, SimilarWeb, Ubersuggest, SpyFu, Serpstat), Google Search (google.com and the top-12 international Google ccTLDs), and the marketplaces we aggregate (Collaborator, WhitePress, Serpzilla, PRNews.io, Adsy, Linkhouse, Bazoom, Links-Stream, FatGrid, LinkBuilder, LinkDetective, Link-Finder). Gmail, Google Docs, Google Drive, Google Maps, and YouTube are explicitly excluded.</li>
            </ul>
          </Section>

          <Section title="4. What we do not collect">
            <ul className="list-disc pl-6 space-y-1.5 text-slate-700">
              <li>Page content, form inputs, keystrokes, or mouse movements</li>
              <li>Full URLs or URL parameters</li>
              <li>Cookies from other sites</li>
              <li>Your browsing history</li>
              <li>Your name, email, or phone number, unless you make an account</li>
              <li>Financial information. We never handle payments.</li>
            </ul>
          </Section>

          <Section title="5. How we use the data">
            <ul className="list-disc pl-6 space-y-1.5 text-slate-700">
              <li>To show you price comparisons for the domains you look up.</li>
              <li>To improve the service. We track popular domains and seller performance in aggregate.</li>
              <li>To earn affiliate commissions. When you click through and buy from a marketplace, they pay us a referral fee. The marketplace sees a referral ID, not you. That fee is what keeps the tool free.</li>
              <li>To prevent abuse. Rate limiting uses IP addresses.</li>
            </ul>
          </Section>

          <Section title="6. Third parties">
            <p className="mb-3">The site and extension talk to a few external services:</p>
            <ul className="list-disc pl-6 space-y-1.5 text-slate-700">
              <li>Marketplace partners like Collaborator and WhitePress. When you click through to buy, you leave LinkDiry and their privacy policy takes over.</li>
              <li>google.com/s2/favicons. We load marketplace icons via Google's public favicon service. Google sees the request, but nothing about you is passed.</li>
              <li>api.country.is. The homepage uses it to detect your country so we can show a local site count. One request, no account.</li>
              <li>Vercel, our hosting provider. It receives the usual HTTP request logs any web host would.</li>
            </ul>
          </Section>

          <Section title="7. Retention">
            <ul className="list-disc pl-6 space-y-1.5 text-slate-700">
              <li>Anonymous lookup logs: 90 days, then aggregated or deleted.</li>
              <li>Click events: kept indefinitely as aggregate analytics. No personal identifier.</li>
              <li>Account data: kept until you delete your account, then removed within 30 days.</li>
            </ul>
          </Section>

          <Section title="8. Your rights">
            <p>Email us at the address below to see what we have on you, correct it, or ask us to delete it. If you have an account, you can also delete the account from your settings, which removes the associated data within 30 days.</p>
          </Section>

          <Section title="9. Children">
            <p>LinkDiry is not built for children under 13 and we do not knowingly collect data from them. If you think a child gave us data, tell us and we will delete it.</p>
          </Section>

          <Section title="10. Changes">
            <p>If we change this policy in a material way, we will update the date at the top. If you have an account, we will also email you. Using the service after a change means you accept the update.</p>
          </Section>

          <Section title="11. Contact">
            <p className="mb-3">Questions or requests go to:</p>
            <a
              href="mailto:privacy@linkdiry.com"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-xl transition-colors"
            >
              <Mail className="w-4 h-4" /> privacy@linkdiry.com
            </a>
          </Section>

        </div>

        <div className="mt-12 pt-8 border-t border-slate-200">
          <Link to="/" className="text-brand-600 font-semibold hover:underline">← Back to LinkDiry</Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-xl font-bold text-slate-900 mb-3">{title}</h2>
      <div className="text-slate-700 leading-relaxed">{children}</div>
    </section>
  );
}
